import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { bool, func, number } from 'prop-types';
import * as R from 'ramda';
import { MDialog } from 'common';
import { useGetCustomerAuthorizationQuery } from '../customerAuthorizationsEndpoints';
import CustomerInformation from './CustomerInformation';
import Terminals from './Terminals';
import Products from './products/Products';
import Destinations from './destinations/Destinations';
import Carriers from './Carriers';
import useCustomerAuthorizationEditor from './useCustomerAuthorizationEditor';

const EditDrawer = ({
  customerAuthorizationId,
  setCustomerAuthorizationId,
  refetchToggle,
  setRefetchToggle,
}) => {
  const params = { customerAuthorizationId };
  const { data: customerAuthorization = {}, isFetching } = useGetCustomerAuthorizationQuery(
    { params },
    // refetch on mount since even though you remove said cache
    // it still doesn't refetch if the input didn't change
    // and shows old data
    { refetchOnMountOrArgChange: true, skip: R.isNil(customerAuthorizationId) }
  );
  const {
    customerAuth,
    enableSave,
    handleClose,
    handleDiscard,
    handleSave,
    hasErrors,
    isSaving,
    openDiscardDialog,
    setCustomerAuth,
    setOpenDiscardDialog,
    setValidation,
    validation,
  } = useCustomerAuthorizationEditor({
    customerAuthorization,
    onClose: () => setCustomerAuthorizationId(null),
    onSaveSuccess: () => setRefetchToggle(!refetchToggle),
  });

  return (
    <>
      <Drawer
        anchor="right"
        onClose={() => handleClose()}
        open={R.not(R.isNil(customerAuthorizationId))}
        slotProps={{
          paper: {
            sx: {
              overflowY: 'hidden',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              width: { xs: 1, lg: 760 },
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            boxShadow: 1,
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            zIndex: 1,
          }}
        >
          <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
            Edit Authorization
          </Typography>
          <IconButton
            aria-label="closeIcon"
            onClick={() => handleClose()}
            sx={{
              alignSelf: 'flex-end',
            }}
          >
            <CloseRoundedIcon sx={{ color: 'primary.light' }} />
          </IconButton>
        </Box>
        {isFetching || isSaving ? (
          <CircularProgress
            size={48}
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              left: { xs: 'calc(50% - 24px)', lg: 'calc(50% - 32px)' },
              position: 'absolute',
              top: 'calc(50% - 64px)',
            }}
          />
        ) : (
          <Box
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              },
              height: 'calc(100% - 129px)',
              overflowY: 'auto',
            }}
          >
            <CustomerInformation
              customerAuth={customerAuth}
              setCustomerAuth={setCustomerAuth}
              setValidation={setValidation}
              validation={validation}
            />
            <Terminals info={customerAuth} setInfo={setCustomerAuth} />
            <Products
              info={customerAuth}
              setInfo={setCustomerAuth}
              setValidation={setValidation}
              validation={validation}
            />
            <Destinations
              info={customerAuth}
              setInfo={setCustomerAuth}
              setValidation={setValidation}
              validation={validation}
            />
            <Carriers info={customerAuth} setInfo={setCustomerAuth} />
          </Box>
        )}
        <Stack
          spacing={1}
          sx={{
            alignItems: 'center',
            backgroundColor: 'surface.white',
            bottom: 0,
            boxShadow: 0,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            position: 'fixed',
            px: 3,
            py: 2,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: 1, lg: 760 },
            zIndex: 1,
          }}
        >
          <Typography sx={{ color: 'error.main', pt: '2px' }} variant="caption">
            {hasErrors && 'Please remove errors before continuing'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClose}
              sx={{
                mr: 2,
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={!enableSave}
              onClick={handleSave}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => setOpenDiscardDialog(false)}
        leftButtonText="Keep Editing"
        onClose={() => setOpenDiscardDialog(false)}
        open={openDiscardDialog}
        rightButtonAction={handleDiscard}
        rightButtonText="Continue"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved customer authorization changes.
        </Typography>
      </MDialog>
    </>
  );
};

EditDrawer.propTypes = {
  customerAuthorizationId: number,
  refetchToggle: bool.isRequired,
  setCustomerAuthorizationId: func.isRequired,
  setRefetchToggle: func.isRequired,
};

EditDrawer.defaultProps = {
  customerAuthorizationId: null,
};

export default EditDrawer;
